import { useEffect, useId, useMemo, useState } from 'react'
import BuilderSegmentedControl from './BuilderSegmentedControl'
import CollapseSlot from './CollapseSlot'
import { useTranslation } from 'react-i18next'
import {
  DISPLAY_GRADES,
  compactPartsAuthenticity,
  visiblePartIds,
  type PartId,
  type PartStatus,
  type PartsAuthenticityMap,
} from '@redevicer/shared'

const FORM_STATUSES: PartStatus[] = ['original', 'replaced_oem', 'replaced_aftermarket', 'unknown']

/** Desktop Būklė — left / right columns (mockup). */
const BUILDER_COLUMN_LEFT: PartId[] = ['display', 'housing', 'camera']
const BUILDER_COLUMN_RIGHT: PartId[] = ['back_glass', 'face_id']

function builderStatusLabel(st: PartStatus, t: (key: string, opts?: { defaultValue?: string }) => string): string {
  if (st === 'replaced_oem') {
    return t('partsAuth.statusSegment.replaced_oem', { defaultValue: 'OEM' })
  }
  if (st === 'replaced_aftermarket') {
    return t('partsAuth.statusSegment.replaced_aftermarket', { defaultValue: 'Aftermarket' })
  }
  return t(`partsAuth.status.${st}`)
}

type Props = {
  value: PartsAuthenticityMap
  onChange: (next: PartsAuthenticityMap) => void
  manufacturer?: string | null
  modelName?: string | null
  defaultCollapsed?: boolean
  /** Desktop sell builder: always open, no fold chrome. */
  builderFlat?: boolean
  /** Hide parts from the grid (e.g. battery → Battery Transparency step). */
  omitPartIds?: PartId[]
}

export default function PartsAuthenticityFields({
  value,
  onChange,
  manufacturer,
  modelName,
  defaultCollapsed = false,
  builderFlat = false,
  omitPartIds = [],
}: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(!defaultCollapsed)
  const [displayGradeListOpen, setDisplayGradeListOpen] = useState(false)
  const panelOpen = builderFlat ? true : open
  const omit = useMemo(() => new Set(omitPartIds), [omitPartIds])
  const ids = visiblePartIds(manufacturer, modelName, value).filter((id) => !omit.has(id))
  const builderLeftIds = BUILDER_COLUMN_LEFT.filter((id) => ids.includes(id))
  const builderRightIds = BUILDER_COLUMN_RIGHT.filter((id) => ids.includes(id))
  const renderIds = builderFlat ? [...builderLeftIds, ...builderRightIds] : ids
  const displayGradeGroupId = useId()

  useEffect(() => {
    if (!builderFlat) return
    document.documentElement.classList.toggle('sell-builder-aftermarket-grades-open', displayGradeListOpen)
    return () => document.documentElement.classList.remove('sell-builder-aftermarket-grades-open')
  }, [builderFlat, displayGradeListOpen])

  function setPart(id: PartId, status: PartStatus) {
    const prev = value[id]
    onChange(
      compactPartsAuthenticity({
        ...value,
        [id]: {
          status,
          grade:
            status === 'replaced_aftermarket' && id === 'display'
              ? prev?.grade || 'premium_oled'
              : null,
        },
      }),
    )
  }

  function setDisplayGrade(grade: string) {
    onChange(
      compactPartsAuthenticity({
        ...value,
        display: { status: 'replaced_aftermarket', grade },
      }),
    )
    if (builderFlat) setDisplayGradeListOpen(false)
  }

  function renderBuilderDisplayGradePanel(grade: string) {
    return (
      <div className="parts-auth-form__aftermarket-inflow" role="group" aria-label={t('partsAuth.formGradeLead')}>
        <ul className="parts-auth-form__aftermarket-list">
          {DISPLAY_GRADES.map((g) => {
            const on = grade === g
            return (
              <li key={g}>
                <label
                  className={`parts-auth-form__aftermarket-option${on ? ' parts-auth-form__aftermarket-option--on' : ''}`}
                >
                  <input
                    type="radio"
                    name={displayGradeGroupId}
                    className="parts-auth-form__aftermarket-radio"
                    checked={on}
                    onChange={() => setDisplayGrade(g)}
                  />
                  <span className="parts-auth-form__aftermarket-option-text">{t(`partsAuth.displayGrade.${g}`)}</span>
                </label>
              </li>
            )
          })}
        </ul>
      </div>
    )
  }

  function renderPart(id: PartId, opts?: { rowLast?: boolean }) {
    const entry = value[id]
    const status = entry?.status ?? 'unknown'
    const displayAftermarket = id === 'display' && status === 'replaced_aftermarket'
    const partLabel = t(`partsAuth.part.${id}`)
    return (
      <div
        key={id}
        className={`parts-auth-form__part${builderFlat ? ' parts-auth-form__part--builder-row' : ''}${
          opts?.rowLast ? ' parts-auth-form__part--row-last' : ''
        }${displayAftermarket && builderFlat && displayGradeListOpen ? ' parts-auth-form__part--dropdown-open' : ''}${
          id === 'display' ? ' parts-auth-form__part--display' : ''
        }`}
      >
        <div className="parts-auth-form__row-label">
          <span className="parts-auth-form__label">{partLabel}</span>
        </div>
        <div className="parts-auth-form__part-fields">
          {builderFlat ? (
            <div className="parts-auth-form__segment-stack">
              <BuilderSegmentedControl
                value={status}
                ariaLabel={partLabel}
                options={FORM_STATUSES.map((st) => ({
                  value: st,
                  label:
                    id === 'display' &&
                    st === 'replaced_aftermarket' &&
                    status === 'replaced_aftermarket'
                      ? t(`partsAuth.displayGrade.${entry?.grade || 'premium_oled'}`)
                      : builderStatusLabel(st, t),
                  segmentClass: `parts-auth-form__segment--${st}`,
                }))}
                onChange={(st) => {
                  setPart(id, st as PartStatus)
                  if (id === 'display') {
                    if (st === 'replaced_aftermarket') setDisplayGradeListOpen(true)
                    else setDisplayGradeListOpen(false)
                  }
                }}
              />
              {id === 'display' && displayAftermarket && displayGradeListOpen ? (
                <div className="parts-auth-form__aftermarket-drop">
                  {renderBuilderDisplayGradePanel(entry?.grade || 'premium_oled')}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="parts-auth-form__chips" role="group" aria-label={partLabel}>
              {FORM_STATUSES.map((st) => {
                const on = status === st
                return (
                  <button
                    key={st}
                    type="button"
                    className={`parts-auth-form__chip parts-auth-form__chip--${st}${on ? ' parts-auth-form__chip--on' : ''}`}
                    aria-pressed={on}
                    onClick={() => setPart(id, st)}
                  >
                    <span className="parts-auth-form__chip-text">{t(`partsAuth.status.${st}`)}</span>
                  </button>
                )
              })}
            </div>
          )}
        </div>
        {displayAftermarket && !builderFlat ? (
          <>
            <p className="parts-auth-form__hint">{t('partsAuth.formGradeLead')}</p>
            <div className="parts-auth-form__chips parts-auth-form__chips--grade" role="group">
              {DISPLAY_GRADES.map((grade) => {
                const on = (entry?.grade || 'premium_oled') === grade
                return (
                  <button
                    key={grade}
                    type="button"
                    className={`parts-auth-form__chip${on ? ' parts-auth-form__chip--on' : ''}`}
                    aria-pressed={on}
                    onClick={() => setDisplayGrade(grade)}
                  >
                    {t(`partsAuth.displayGrade.${grade}`)}
                  </button>
                )
              })}
            </div>
            <p className="parts-auth-form__explain">
              {t(`partsAuth.explain.display.${entry?.grade || 'premium_oled'}`)}
            </p>
          </>
        ) : !builderFlat ? (
          <p className="parts-auth-form__explain">{t(`partsAuth.explain.${status}`)}</p>
        ) : null}
      </div>
    )
  }

  return (
    <div
      className={`parts-auth-form${panelOpen ? '' : ' parts-auth-form--folded'}${
        builderFlat ? ' parts-auth-form--builder-detail' : ''
      }${displayGradeListOpen ? ' parts-auth-form--aftermarket-menu-open' : ''}`}
    >
      {!builderFlat ? (
        <button
          type="button"
          className="listing-fold-head"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={panelOpen}
        >
          <span className="parts-auth-form__title">{t('partsAuth.title')}</span>
          <span className={`listing-fold-chev${panelOpen ? ' listing-fold-chev--open' : ''}`} aria-hidden="true" />
        </button>
      ) : null}
      {builderFlat ? (
        <div className="parts-auth-form__builder-body">
          <div className="parts-auth-form__builder-columns">
            <div className="parts-auth-form__builder-col">
              {builderLeftIds.map((id, i) =>
                renderPart(id, { rowLast: i === builderLeftIds.length - 1 }),
              )}
            </div>
            <div className="parts-auth-form__builder-col">
              {builderRightIds.map((id, i) =>
                renderPart(id, { rowLast: i === builderRightIds.length - 1 }),
              )}
            </div>
          </div>
        </div>
      ) : (
        <CollapseSlot open={panelOpen}>
          <p className="parts-auth-form__lead">{t('partsAuth.formLead')}</p>
          {renderIds.map((id) => renderPart(id))}
        </CollapseSlot>
      )}
    </div>
  )
}
